import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { io } from "socket.io-client";
import { URL } from "../../store/utils";
import { handleSocketSessionExpiry } from "../../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "../useLatestToken";
import {
  readPersistedFilters,
  writePersistedFilters,
} from "../usePersistedFilters";
import logger from "../../utils/logger";

// Kapsam seçimi sekme boyunca hatırlanır: dashboard'dan çıkıp dönünce
// kullanıcı her seferinde "Tümü"ye düşmesin. sessionStorage sekmeye özel,
// sekme kapanınca temizlenir (bkz. usePersistedFilters).
const SCOPE_STORAGE_KEY = "dealerDashboard:scope";
const VALID_SCOPES = ["tumu", "bayim", "bayilerim", "musterilerim"];

export const useDashboard = (token) => {
  const { hasToken, tokenRef } = useLatestToken(token);
  const socketRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const [consumptionData, setConsumptionData] = useState(null);
  const [consumptionLoading, setConsumptionLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [scope, setScope] = useState(() => {
    const saved = readPersistedFilters(SCOPE_STORAGE_KEY);
    return VALID_SCOPES.includes(saved?.scope) ? saved.scope : "tumu";
  });
  const [performanceData, setPerformanceData] = useState({});
  const [activeConsumption, setActiveConsumption] = useState("today");

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const startLoadingTimeout = useCallback(() => {
    clearLoadingTimeout();
    loadingTimeoutRef.current = setTimeout(() => {
      setConsumptionLoading(false);
      setConnectionError(true);
    }, 15000);
  }, [clearLoadingTimeout]);

  useEffect(() => {
    if (socketConnected && socketRef.current?.connected) {
      socketRef.current.emit("getDashboardData", {
        section: "Firma",
        scope,
      });
      socketRef.current.emit("getConsumptionPerformanceData", {
        section: "Firma",
        scope,
      });
      startLoadingTimeout();
    }
  }, [socketConnected, scope, startLoadingTimeout]);

  useEffect(() => {
    if (!hasToken) return;

    if (
      socketRef.current &&
      (socketRef.current.connected || socketRef.current.connecting)
    ) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(URL.url, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      query: { token: tokenRef.current },
    });

    attachTokenOnReconnect(socketRef.current, tokenRef); // #142

    // İlk veri istekleri "connect" değil "authenticated" event'ine bağlanır —
    // server handler'ları auth bittikten sonra kurar, erken emit sessizce düşer.
    socketRef.current.on("authenticated", () => {
      setSocketConnected(true);
      setConnectionError(false);
    });

    socketRef.current.on("connect_error", (error) => {
      logger.error("Socket.IO bağlantı hatası:", error.message);
      setConsumptionLoading(false);
      setConnectionError(true);
    });

    socketRef.current.on("dashboardData", (data) => {
      clearLoadingTimeout();
      setConsumptionData(data);
      setConsumptionLoading(false);
      setConnectionError(false);
    });

    // Tüketim performansı (tüm periyotlar tek payload) — yüzde iyi/kötü
    // rozetlerini besler. Sunucu erişimi dashboard.read ile açık.
    socketRef.current.on("consumptionPerformanceData", (payload) => {
      setPerformanceData(payload?.performance || {});
    });

    socketRef.current.on("error", (payload) => {
      const message = payload?.message ?? "Socket hatası";
      const reason = payload?.reason;
      clearLoadingTimeout();
      setConsumptionLoading(false);
      setSocketConnected(false);
      if (reason && handleSocketSessionExpiry(reason)) return;
      setConnectionError(true);
      logger.error("Socket hatası:", message);
    });

    socketRef.current.on("disconnect", () => {
      setSocketConnected(false);
    });

    return () => {
      clearLoadingTimeout();
      if (socketRef.current) {
        setSocketConnected(false);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [hasToken, tokenRef, clearLoadingTimeout]);

  const prevScopeRef = useRef(scope);
  useEffect(() => {
    // Sadece scope değiştiğinde emit et (ilk bağlantı timeout effect'inde yapılıyor)
    if (prevScopeRef.current === scope) return;
    prevScopeRef.current = scope;
    if (socketRef.current && socketRef.current.connected) {
      setConsumptionLoading(true);
      setConnectionError(false);
      socketRef.current.emit("getDashboardData", {
        section: "Firma",
        scope,
      });
      socketRef.current.emit("getConsumptionPerformanceData", {
        section: "Firma",
        scope,
      });
      startLoadingTimeout();
    }
  }, [scope, socketConnected, startLoadingTimeout]);

  const handleScopeChange = useCallback((newScope) => {
    setScope(newScope);
    writePersistedFilters(SCOPE_STORAGE_KEY, { scope: newScope });
    setConsumptionLoading(true);
  }, []);

  const handleClickConsumption = useCallback((choice) => {
    setActiveConsumption(choice);
  }, []);

  const monthlyConsumption = useMemo(() => {
    return consumptionData?.monthlyConsumption || [];
  }, [consumptionData?.monthlyConsumption]);

  const getConsumptionData = useCallback(() => {
    if (!consumptionData) return null;
    switch (activeConsumption) {
      case "today":
        return consumptionData.todayConsumption;
      case "yesterday":
        return consumptionData.yesterdayConsumption;
      case "thisweek":
        return consumptionData.thisWeekConsumption;
      case "thismonth":
        return consumptionData.thisMonthConsumption;
      default:
        return consumptionData.thisYearConsumption;
    }
  }, [consumptionData, activeConsumption]);

  return {
    scope,
    handleScopeChange,
    activeConsumption,
    handleClickConsumption,
    consumptionLoading,
    consumptionData,
    connectionError,
    monthlyConsumption,
    getConsumptionData,
    performance: performanceData,
    deviceBreakdown: consumptionData?.deviceBreakdown || null,
    firmBreakdown: consumptionData?.firmBreakdown || null,
  };
};
